/**
 * EVO — hisob-faktura ma'lumot qatlami (`/api/invoices`).
 *
 * ============================================================================
 * NIMA UCHUN `lib/sales.ts` DA EMAS
 * ============================================================================
 * U yerda `InvoiceListRow` va `fetchInvoices(salesOrderId)` bor, lekin ular
 * BUYURTMA KARTOCHKASI uchun: bitta buyurtmaning hisob-fakturalari, 50 ta
 * bilan cheklangan, filtrsiz va sahifalashsiz. Bu yerdagi ekran esa butun
 * reyestrni ochadi — sahifalash, saralash, holat/davr/qarz filtrlari bilan.
 *
 * Ikkalasini bitta funksiyaga birlashtirish rad etildi: `lib/sales.ts` bugun
 * buyurtma ekranining chunk'iga kiradi va unga reyestr filtrlarini qo'shish
 * sotuvchining buyurtma ekranini og'irlashtirardi, hech qanday foyda bermay.
 *
 * ============================================================================
 * KO'LAM UI DA TAKRORLANMAYDI
 * ============================================================================
 * Sotuvchi FAQAT o'z mijozining hisob-fakturasini ko'radi va bu SERVERDA
 * (`invoices.service.ts` → `ownFilter()`, `so.salesperson_id = me or
 * so.created_by = me`). Bu yerda hech qanday qo'shimcha filtr yo'q: UI
 * ro'yxatni kesib tashlasa, u ikkinchi haqiqat manbaiga aylanadi.
 *
 * JONLI TEKSHIRILGAN (bugun):
 *   admin    → `GET /invoices` 200, `total: 35`
 *   sotuvchi → 200, `total: 4` (faqat o'zi yaratgan mijozlar)
 *   sotuvchi → `GET /invoices/3` (begona mijoz) 404 «Yozuv topilmadi»
 *   ombor    → 403 «Bu amal uchun ruxsatingiz yo'q»
 *
 * 404 (403 emas) ATAYLAB: ko'lamdan tashqaridagi hujjatning MAVJUDLIGINI
 * tasdiqlash ham ma'lumot berish demak (`scope.ts` §9).
 */
import { api } from './api';
import type { InvoiceCredit } from './credit-notes';
import { isPositiveDecimal, isZeroDecimal, subDecimals } from './decimal';
import type { Page, SortDir } from './products';

/** `packages/db/src/schema/sales.ts` → `INVOICE_STATUS`. */
export const INVOICE_STATUSES = [
  'draft',
  'issued',
  'partially_paid',
  'paid',
  'cancelled',
] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export interface InvoiceRow {
  readonly id: number;
  readonly docNo: string;
  readonly customerId: number;
  readonly customerName: string;
  readonly invoiceDate: string;
  readonly dueDate: string;
  readonly currencyCode: string;
  /**
   * JAMI — QQS BILAN (gross).
   *
   * Soliq bu summaning ICHIDA, ustida emas: `balanceAmount`, AR aging va
   * buxgalteriya sverkasi aynan shu ustundan quriladi (migratsiya 0020).
   * Ya'ni QQS qo'shilgani bilan mijoz to'laydigan raqam o'zgarmagan.
   */
  readonly totalAmount: string;
  /** Jami ichidagi soliq. `"0.0000"` — SOLIQSIZ, «to'ldirilmagan» emas. */
  readonly vatAmount: string;
  readonly paidAmount: string;
  /**
   * KREDIT-NOTA bilan yopilgan qism. `paidAmount` GA KIRMAYDI.
   *
   * ╔══════════════════════════════════════════════════════════════════════════╗
   * ║ USIZ REYESTRDAGI UCH USTUN QO'SHILMASDI                                  ║
   * ║                                                                          ║
   * ║ Bazada `ck_invoices_balance` turibdi: `total = paid + credited +         ║
   * ║ balance`. Ekranda esa uchtadan bittasi YO'Q edi, ya'ni tenglama          ║
   * ║ o'qiydigan tomonda YOPILMASDI — kreditlangan satr «summa $20,            ║
   * ║ to'langan $0, qoldiq $10» bo'lib ko'rinardi va farqni tushuntiradigan    ║
   * ║ ustun jadvalda umuman yo'q edi.                                          ║
   * ║                                                                          ║
   * ║ Server buni ro'yxat satrida HAM yuboradi (`InvoiceListRow.creditedAmount ║
   * ║ — `invoices` jadvalining o'z ustuni, qo'shimcha so'rovsiz).              ║
   * ║ JONLI TEKSHIRILGAN: `GET /invoices?q=INV-2026-00079` →                   ║
   * ║ `total 20.0000 = paid 0.0000 + credited 10.0000 + balance 10.0000`.      ║
   * ╚══════════════════════════════════════════════════════════════════════════╝
   */
  readonly creditedAmount: string;
  /** Qolgan qarz — hisob-fakturaning O'Z valyutasida. Soliq bilan BIRGA. */
  readonly balanceAmount: string;
  readonly status: string;
  /** MANFIY = muddat hali kelmagan. Serverda `current_date - due_date`. */
  readonly daysOverdue: number;
}

export interface InvoiceLine {
  readonly id: number;
  readonly deliveryLineId: number;
  readonly productId: number;
  readonly sku: string;
  readonly name: string;
  readonly quantity: string;
  readonly unitPrice: string;
  readonly lineAmount: string;
  readonly lineBase: string;
  /**
   * Hujjat yozilgan KUNDAGI stavka — mahsulot kartochkasidan NUSXA.
   *
   * Kartochkadagi stavka keyin o'zgarsa ham bu qiymat o'zgarmaydi
   * (`catalog.ts`: kartochka «haqiqat manbai emas»). Shuning uchun ekranda
   * ham aynan SATRDAGI stavka ko'rsatiladi, mahsulotniki emas.
   */
  readonly vatRate: string;
  readonly vatAmount: string;
}

export interface Invoice extends InvoiceRow {
  readonly salesOrderId: number;
  readonly salesOrderNo: string;
  readonly legalEntityId: number;
  /** Hujjatga MUHRLANGAN kurs. Kunlik kurs keyin o'zgarsa ham o'zgarmaydi. */
  readonly fxRate: string;
  readonly totalBase: string;
  /**
   * Baza valyutadagi (USD) soliq.
   *
   * Bu `vatAmount × fxRate` EMAS: server uni ALOHIDA hisoblaydi, chunki ikki
   * marta yaxlitlash bir tiyin farq beradi va o'sha tiyin buxgalteriya
   * sverkasida chiqib qolardi (`invoices.service.ts` dagi ramka). Shuning
   * uchun brauzerda ham qayta hisoblanmaydi — serverdan kelgani ko'rsatiladi.
   */
  readonly vatBase: string;
  /**
   * Hujjatning EFFEKTIV stavkasi.
   *
   * Aralash hujjatda (12% li va 0% li tovar birga) u satrlardagi
   * stavkalarning hech biriga teng emas — masalan $1 120 (12%) + $1 000 (0%)
   * hujjatda u 6.00%. Bu MA'LUMOT: soliq hisobi `vatAmount` bo'yicha
   * yuritiladi, hech qayerda `total × vatRate` qayta hisoblanmaydi.
   */
  readonly vatRate: string;
  /** `totalAmount − vatAmount`. Serverda QOLDIQ sifatida olinadi. */
  readonly netAmount: string;
  readonly netBase: string;
  readonly notes: string | null;
  readonly lines: readonly InvoiceLine[];
  /**
   * «Qaysi hujjat yopdi» — kredit-notalar.
   *
   * ╔══════════════════════════════════════════════════════════════════════════╗
   * ║ `null` VA `[]` BIR XIL EMAS — SERVER FARQNI ATAYLAB YUBORADI             ║
   * ║                                                                          ║
   * ║   `[]`   — kredit YO'Q (faktura kreditlanmagan);                         ║
   * ║   `null` — hujjatlar ro'yxati foydalanuvchiga KO'RSATILMADI.             ║
   * ║                                                                          ║
   * ║ Ikkalasini `?? []` bilan tenglashtirish TAQIQLANADI: o'shanda            ║
   * ║ kreditlangan fakturada «kredit yo'q» degan ekran chiqardi, ya'ni ekran   ║
   * ║ O'Z RAQAMIGA (`creditedAmount`) zid bo'lardi. Qaror `creditsDisplay()`   ║
   * ║ da bir joyda turadi va testda qulflangan.                               ║
   * ║                                                                          ║
   * ║ Manba: `invoices.service.ts` → `creditsVisibility()`.                    ║
   * ╚══════════════════════════════════════════════════════════════════════════╝
   */
  readonly credits: readonly InvoiceCredit[] | null;
}

export interface InvoiceListParams {
  readonly page: number;
  readonly pageSize: number;
  readonly sortBy?: string | undefined;
  readonly sortDir: SortDir;
  readonly q?: string | undefined;
  readonly status?: InvoiceStatus | undefined;
  readonly customerId?: number | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly openOnly?: boolean | undefined;
  readonly overdue?: boolean | undefined;
}

/** `INVOICE_SORTABLE` (invoices.service.ts) bilan AYNAN bir xil oq ro'yxat. */
export const INVOICE_SORTABLE = [
  'docNo',
  'invoiceDate',
  'dueDate',
  'totalAmount',
  'balanceAmount',
  'status',
] as const;

export const invoiceKeys = {
  all: ['invoices'] as const,
  list: (params: InvoiceListParams) => ['invoices', 'list', params] as const,
  detail: (id: number) => ['invoices', 'detail', id] as const,
};

export function fetchInvoiceList(
  params: InvoiceListParams,
  signal?: AbortSignal,
): Promise<Page<InvoiceRow>> {
  return api.get<Page<InvoiceRow>>('/invoices', {
    query: {
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy,
      sortDir: params.sortDir,
      q: params.q,
      status: params.status,
      customerId: params.customerId,
      from: params.from,
      to: params.to,
      // Bayroqlar FAQAT yoqilganda yuboriladi: `openOnly=false` server uchun
      // ham «filtrsiz» degani, lekin u URL ni va query kalitini uzaytiradi.
      openOnly: params.openOnly ? 'true' : undefined,
      overdue: params.overdue ? 'true' : undefined,
    },
    ...(signal ? { signal } : {}),
  });
}

export function fetchInvoice(id: number, signal?: AbortSignal): Promise<Invoice> {
  return api.get<Invoice>(`/invoices/${id}`, { ...(signal ? { signal } : {}) });
}

/**
 * Hisob-faktura KECHIKKANMI.
 *
 * Ikkita shart va ikkalasi ham zarur: muddat o'tgan BO'LSA HAM qarz nolga
 * teng bo'lsa, u kechikkan emas — to'langan. Faqat `daysOverdue > 0` ga
 * tayanish yopilgan hujjatlarni ham qizartirardi va rang ma'nosini
 * yo'qotardi (`ar-aging.tsx` dagi «nol qiymatda bo'yalmaydi» bilan bir xil
 * mulohaza).
 *
 * Solishtirish `decimal.ts` orqali: `balanceAmount !== '0'` yozib bo'lmaydi,
 * chunki server `"0.0000"` yuboradi va satr taqqoslash uni «nol emas» deb
 * o'qiydi.
 */
export function isOverdue(row: Pick<InvoiceRow, 'daysOverdue' | 'balanceAmount'>): boolean {
  return row.daysOverdue > 0 && isPositiveDecimal(row.balanceAmount);
}

// ---------------------------------------------------------------------------
// Balans tenglamasi
// ---------------------------------------------------------------------------

/** Tenglamani tashkil qiluvchi to'rt ustun — kartochkada ham, reyestrda ham bor. */
export type InvoiceBalanceParts = Pick<
  InvoiceRow,
  'totalAmount' | 'paidAmount' | 'creditedAmount' | 'balanceAmount'
>;

/**
 * `total − paid − credited − balance`. Nol bo'lsa — tenglama YOPILADI.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ NIMA UCHUN BRAUZERDA HISOBLANADI — VA NIMA UCHUN BU QOIDANI BUZMAYDI     ║
 * ║                                                                          ║
 * ║ `format.ts` qoidasi: arifmetika serverda. Bu yerdagi ayirma esa EKRANGA  ║
 * ║ CHIQADIGAN summa emas — u NAZORAT: to'rttala raqam ham serverdan xuddi   ║
 * ║ shundayligicha ko'rsatiladi, ayirma faqat «bu to'rtta raqam bir-biriga   ║
 * ║ mos keladimi» degan savolga javob beradi. Aynan shu naqsh ekranda        ║
 * ║ allaqachon bor (`FxGapNotice` → `subDecimals`), faqat u KREDITNI hisobga ║
 * ║ olmasdi.                                                                ║
 * ║                                                                          ║
 * ║ `subDecimals` BigInt ustida ishlaydi va yaxlitlamaydi, ya'ni bu yerda    ║
 * ║ float xatosi paydo bo'lishi mumkin emas (`decimal.ts` fayl boshidagi     ║
 * ║ sabab).                                                                 ║
 * ║                                                                          ║
 * ║ `null` — «HISOBLAB BO'LMADI» (qiymatlardan biri buzuq yoki kelmagan).    ║
 * ║ U nolga tenglashtirilmaydi: «tenglama yopildi» degan XATO xulosa         ║
 * ║ chiqarardi (`isZeroDecimal` izohidagi «nol ≠ ma'lumot yo'q» qoidasi).    ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
export function invoiceBalanceDrift(parts: InvoiceBalanceParts): string | null {
  const afterPaid = subDecimals(parts.totalAmount, parts.paidAmount);
  const afterCredit = subDecimals(afterPaid, parts.creditedAmount);
  return subDecimals(afterCredit, parts.balanceAmount);
}

/** Tenglama yopiladimi. Hisoblab bo'lmasa — `false` («bilmayman» ≠ «to'g'ri»). */
export function invoiceBalanceCloses(parts: InvoiceBalanceParts): boolean {
  return isZeroDecimal(invoiceBalanceDrift(parts));
}

/**
 * Kredit bloki EKRANDA qanday chiziladi.
 *
 *   `hidden` — hujjatlar ro'yxati ko'rsatilmadi, lekin SUMMA bor: ekran buni
 *              ochiq aytadi, aks holda kartochka o'z raqamiga zid bo'lardi;
 *   `none`   — ko'rsatadigan narsa yo'q, blok UMUMAN chizilmaydi;
 *   `list`   — hujjatlar jadvali.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ NIMA UCHUN SOF FUNKSIYA                                                   │
 * │                                                                          │
 * │ Uch holatning ikkitasi bir xil BO'SHLIK bo'lib ko'rinadi (`hidden` va    │
 * │ `none`) va aynan shuning uchun ular bir kun jimgina birlashib ketishi    │
 * │ mumkin. Qaror komponent ichida `?? []` bo'lib yotsa, uni test ham,       │
 * │ o'qigan odam ham ko'rmaydi. Serverda ayni shu qaror ayni shu sababdan    │
 * │ sof funksiyada turadi (`creditsVisibility`).                             │
 * │                                                                          │
 * │ `credits === null` bo'lganda summa NOLga teng bo'lsa — yashiriladigan    │
 * │ narsa yo'q: server kredit bo'lmaganda ro'yxatni umuman o'qimaydi         │
 * │ (`creditsVisibility` → `empty`), ya'ni «ko'rsatilmadi» deb yozish bo'sh  │
 * │ joyda ogohlantirish bo'lardi.                                           │
 * │                                                                          │
 * │ Buzuq/kelmagan summada (`isZeroDecimal` → `false`) `hidden` chiqadi:     │
 * │ «bilmayman» holatida ro'yxat yo'qligini TASDIQLAB bo'lmaydi.             │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
export type CreditsDisplay = 'hidden' | 'none' | 'list';

export function creditsDisplay(
  credits: readonly InvoiceCredit[] | null,
  creditedAmount: string,
): CreditsDisplay {
  if (credits === null) return isZeroDecimal(creditedAmount) ? 'none' : 'hidden';
  return credits.length === 0 ? 'none' : 'list';
}
